// features/modal/ui/ContentModal.tsx
import React from 'react';
import styled from 'styled-components';
import { modalStore } from 'entities/modal/model/modal.store';
import { modalContentStore } from 'entities/modal/api/modalContent';
import { observer } from 'mobx-react-lite';
import { ExpiredPurchase } from 'entities/modal/model/types';

const ModalWrapper = styled.div`
  @apply w-full max-w-2xl mx-auto;
  height: 100%;
  display: flex;
  flex-direction: column;
`;

const Title = styled.h2`
  @apply text-2xl font-bold mb-6 text-blue-900;
  flex-shrink: 0;
`;

const ScrollableContent = styled.div`
  @apply flex-1 overflow-y-auto;
  padding-right: 8px;
  min-height: 0; /* Critical for flexbox scrolling */
  
  /* Custom scrollbar styling */
  &::-webkit-scrollbar {
    width: 6px;
  }
  
  &::-webkit-scrollbar-track {
    background: tertiary;
    border-radius: 3px;
  }
  
  &::-webkit-scrollbar-thumb {
    background: tertiary;
    border-radius: 3px;
  }
  
  &::-webkit-scrollbar-thumb:hover {
    background: tertiary;
  }
`;

const Content = styled.div`
  @apply text-gray-700 mb-6 leading-relaxed;
  white-space: pre-wrap; /* Preserve line breaks and spaces */
  
  p {
    @apply mb-4;
  }
  
  strong {
    @apply font-semibold;
  }
  
  /* Style for important text */
  .important {
    @apply font-semibold;
  }
`;

const FormattedTextContent = styled.div`
  @apply text-gray-700 mb-6 leading-relaxed;
  white-space: pre-wrap; /* Preserve line breaks and spaces */
  line-height: 1.6;
  
  /* Handle sections with proper spacing */
  & > div {
    margin-bottom: 1.5rem;
  }
  
  /* Style headings that might be in the text */
  strong {
    @apply font-semibold text-gray-900;
  }
`;

const LoadingWrapper = styled.div`
  @apply flex items-center justify-center;
  height: 50vh;
`;

const LoadingTitle = styled.h2`
  @apply text-xl font-medium text-gray-600;
`;

const NotificationBadge = styled.div`
  @apply bg-red-500 text-white text-sm px-3 py-1 rounded-full mb-4 inline-block;
  flex-shrink: 0;
`;

const ExpiredPurchaseCard = styled.div`
  @apply bg-yellow-50 border border-yellow-200 rounded-lg p-4 mb-4;
`;

const ExpiredPurchaseTitle = styled.h3`
  @apply text-lg font-semibold text-yellow-800 mb-2;
`;

const ExpiredPurchaseDate = styled.p`
  @apply text-sm text-yellow-600;
`;

const ExpiredPurchasesSection = styled.div`
  @apply mb-6;
  flex-shrink: 0;
`;

type ContentModalProps = {
  type: 'privacyPolicy' | 'termsOfService' | 'newsletter' | 'offer';
};

const formatDate = (dateString: string) => {
  return new Date(dateString).toLocaleDateString('ru-RU', {
    year: 'numeric',
    month: 'long',
    day: 'numeric'
  });
};

const ExpiredPurchaseList: React.FC<{ purchases: ExpiredPurchase[] }> = ({ purchases }) => {
  if (purchases.length === 0) return null;

  return (
    <ExpiredPurchasesSection>
      <h3 className="text-lg font-semibold mb-3 text-gray-800">Истекшие покупки:</h3>
      {purchases.map((purchase) => (
        <ExpiredPurchaseCard key={purchase.id}>
          <ExpiredPurchaseTitle>{purchase.purchase_card_title}</ExpiredPurchaseTitle>
          <ExpiredPurchaseDate>
            Срок действия истек: {formatDate(purchase.expiration_date)}
          </ExpiredPurchaseDate>
        </ExpiredPurchaseCard>
      ))}
    </ExpiredPurchasesSection>
  );
};

// Helper function to decode HTML entities and format text
const formatTextContent = (text: string): string => {
  // Decode HTML entities
  const decodeHtmlEntities = (str: string): string => {
    const textArea = document.createElement('textarea');
    textArea.innerHTML = str;
    return textArea.value;
  };

  // First decode HTML entities
  let formattedText = decodeHtmlEntities(text);
  
  // Replace \r\n and \n with actual line breaks
  formattedText = formattedText.replace(/\\r\\n|\\n|\r\n|\n/g, '\n');
  
  // Replace &nbsp; with regular spaces
  formattedText = formattedText.replace(/&nbsp;/g, ' ');
  
  // Replace multiple consecutive line breaks with double line breaks for better spacing
  formattedText = formattedText.replace(/\n\s*\n\s*\n/g, '\n\n');
  
  return formattedText;
};

// Helper function to decode HTML entities for HTML content
const decodeHtmlContent = (htmlString: string): string => {
  const textArea = document.createElement('textarea');
  textArea.innerHTML = htmlString;
  return textArea.value;
};

// Alternative component for plain text rendering
const PlainTextContent: React.FC<{ content: string }> = ({ content }) => {
  const formattedContent = React.useMemo(() => {
    return formatTextContent(content);
  }, [content]);

  return (
    <FormattedTextContent>
      {formattedContent}
    </FormattedTextContent>
  );
};

export const ContentModal: React.FC<ContentModalProps> = observer(({ type }) => {
  const content = modalContentStore.getContent(type);

  React.useEffect(() => {
    if (!content) {
      modalContentStore.fetchContent(type);
    }
  }, [type]);

  if (!content) {
    return (
      <LoadingWrapper>
        <LoadingTitle>Загрузка...</LoadingTitle>
      </LoadingWrapper>
    );
  }

  // Check if content.content contains HTML tags (handle both encoded and unencoded)
  const hasHtmlTags = /<[^>]+>/.test(content.content) || /&lt;[^&]*&gt;/.test(content.content);
  const isHtmlContent = hasHtmlTags;

  return (
    <ModalWrapper>
      <Title className='text-2xl font-semibold text-primary mb-5'>{content.title}</Title>
      
      {/* {content.notification_count > 0 && (
        <NotificationBadge>
          Непрочитанные уведомления: {content.notification_count}
        </NotificationBadge>
      )} */}
      
      <ScrollableContent>
        {isHtmlContent ? (
          <Content dangerouslySetInnerHTML={{ __html: decodeHtmlContent(content.content) }} />
        ) : (
          <PlainTextContent content={content.content} />
        )}
        <ExpiredPurchaseList purchases={content.expired} />
      </ScrollableContent>
    </ModalWrapper>
  );
});